import type { ClassifyResult, LabelingSession, LabelingSessionImage, ImageLabel, LabelingExperiment, LabelingExperimentLabels } from '../types/LabelingTypes';
import type { HttpClient } from '@/lib/http/HttpClient';

const delay = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));

export function labelizeClassify(_http: unknown, key: string): Promise<ClassifyResult> {
  const delayMs = 400 + ((key.length * 17) % 400);
  const hash = key.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const label = hash % 3 === 0 ? 'worn' : ('good' as const);
  const omitConfidence = hash % 10 < 3;
  const result: ClassifyResult = { key, label };
  if (!omitConfidence) result.confidence = 0.65 + (hash % 35) / 100;
  return delay(delayMs).then(() => result);
}

export function labelizeClassifyBatch(_http: unknown, keys: string[]): Promise<ClassifyResult[]> {
  return Promise.all(keys.map((k) => labelizeClassify(_http, k)));
}

// ---------- Session mock data ----------

const MOCK_SESSIONS: Record<string, LabelingSession[]> = {
  'TL-4823': [
    {
      sessionId: 'sess-001',
      experimentId: 'TL-4823',
      name: 'default',
      description: null,
      companyName: 'ToolLife',
      createdBy: 'user@example.com',
      createdAt: '2025-01-10T08:00:00Z',
      updatedAt: '2025-01-10T09:00:00Z',
      labelCount: 1,
    },
  ],
  'TL-0099': [
    {
      sessionId: 'sess-002',
      experimentId: 'TL-0099',
      name: 'default',
      description: null,
      companyName: 'ToolLife',
      createdBy: 'user@example.com',
      createdAt: '2025-01-14T09:00:00Z',
      updatedAt: '2025-01-14T10:00:00Z',
      labelCount: 2,
    },
  ],
};

let sessionCounter = 100;

export function labelizeListSessions(
  _http: HttpClient,
  toolId: string,
  _signal?: AbortSignal
): Promise<LabelingSession[]> {
  return delay(200).then(() => MOCK_SESSIONS[toolId] ?? []);
}

export function labelizeCreateSession(
  _http: HttpClient,
  toolId: string,
  _signal?: AbortSignal
): Promise<LabelingSession> {
  sessionCounter += 1;
  const session: LabelingSession = {
    sessionId: `sess-new-${sessionCounter}`,
    experimentId: toolId,
    name: 'default',
    description: null,
    companyName: 'ToolLife',
    createdBy: 'user@example.com',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    labelCount: 0,
  };
  MOCK_SESSIONS[toolId] = [session];
  return delay(200).then(() => session);
}

const MOCK_MACHINE_BY_EXPERIMENT: Record<string, string> = {
  '10071': 'Micro5',
  '10082': 'CNC Alpha',
  '20031': 'Haas ST-20',
};

// Fixed mock session images using the experiment/part hierarchy from labelizeListExperiments
const RAW_MOCK_SESSION_IMAGES: Omit<LabelingSessionImage, 'url' | 'machineName'>[] = [
  { imageKey: 'TESTID_10071/PARTID_1/STATION_FrontView/OPID_0/Stack_20250905_085652_327_frames', relativePath: 'https://picsum.photos/seed/s01/640/480', filename: 'frame_0001.jpg', experimentId: '10071', partId: '1', operationId: '0', stackId: '20250905_085652_327_frames', createdAt: '2025-09-05T08:56:52Z', label: null },
  { imageKey: 'TESTID_10071/PARTID_1/STATION_FrontView/OPID_5580/Stack_20250905_132342_123_frames', relativePath: 'https://picsum.photos/seed/s02/640/480', filename: 'frame_0001.jpg', experimentId: '10071', partId: '1', operationId: '5580', stackId: '20250905_132342_123_frames', createdAt: '2025-09-05T13:23:42Z', label: null },
  { imageKey: 'TESTID_10071/PARTID_2/STATION_FrontView/OPID_0/Stack_20250906_091011_88_frames', relativePath: 'https://picsum.photos/seed/s03/640/480', filename: 'frame_0001.jpg', experimentId: '10071', partId: '2', operationId: '0', stackId: '20250906_091011_88_frames', createdAt: '2025-09-06T09:10:11Z', label: null },
  { imageKey: 'TESTID_10071/PARTID_2/STATION_FrontView/OPID_5580/Stack_20250906_144500_200_frames', relativePath: 'https://picsum.photos/seed/s04/640/480', filename: 'frame_0001.jpg', experimentId: '10071', partId: '2', operationId: '5580', stackId: '20250906_144500_200_frames', createdAt: '2025-09-06T14:45:00Z', label: null },
  { imageKey: 'TESTID_10071/PARTID_3/STATION_FrontView/OPID_0/Stack_20250907_080000_45_frames', relativePath: 'https://picsum.photos/seed/s05/640/480', filename: 'frame_0001.jpg', experimentId: '10071', partId: '3', operationId: '0', stackId: '20250907_080000_45_frames', createdAt: '2025-09-07T08:00:00Z', label: null },
  { imageKey: 'TESTID_10082/PARTID_1/STATION_FrontView/OPID_0/Stack_20251001_100000_60_frames', relativePath: 'https://picsum.photos/seed/s06/640/480', filename: 'frame_0001.jpg', experimentId: '10082', partId: '1', operationId: '0', stackId: '20251001_100000_60_frames', createdAt: '2025-10-01T10:00:00Z', label: null },
  { imageKey: 'TESTID_20031/PARTID_1/STATION_FrontView/OPID_0/Stack_20251101_090000_100_frames', relativePath: 'https://picsum.photos/seed/s07/640/480', filename: 'frame_0001.jpg', experimentId: '20031', partId: '1', operationId: '0', stackId: '20251101_090000_100_frames', createdAt: '2025-11-01T09:00:00Z', label: null },
  { imageKey: 'TESTID_20031/PARTID_2/STATION_FrontView/OPID_0/Stack_20251102_110000_75_frames', relativePath: 'https://picsum.photos/seed/s08/640/480', filename: 'frame_0001.jpg', experimentId: '20031', partId: '2', operationId: '0', stackId: '20251102_110000_75_frames', createdAt: '2025-11-02T11:00:00Z', label: null },
];

const MOCK_SESSION_IMAGES: LabelingSessionImage[] = RAW_MOCK_SESSION_IMAGES.map((img) => ({
  ...img,
  url: img.relativePath,
  machineName: MOCK_MACHINE_BY_EXPERIMENT[img.experimentId] ?? null,
}));

export function labelizeGetSignedImageUrl(
  _http: HttpClient,
  experimentId: string,
  partId: string,
  operationId: string,
  imageKey: string,
  _signal?: AbortSignal
): Promise<string> {
  return delay(100).then(
    () => `https://rustfs.mock/signed/${experimentId}/${partId}/${operationId}?imageKey=${encodeURIComponent(imageKey)}`
  );
}

export function labelizeListSessionImages(
  _http: HttpClient,
  sessionId: string,
  _signal?: AbortSignal
): Promise<LabelingSessionImage[]> {
  const experimentId = Object.entries(MOCK_SESSIONS).find(([, sessions]) =>
    sessions.some((s) => s.sessionId === sessionId)
  )?.[0];
  const images = experimentId
    ? MOCK_SESSION_IMAGES.filter((img) => img.experimentId === experimentId)
    : MOCK_SESSION_IMAGES.filter((img) => img.experimentId === '10071');
  return delay(300).then(() => images);
}

export function labelizeListExperimentLabels(
  _http: HttpClient,
  experimentId: string,
  _signal?: AbortSignal
): Promise<LabelingExperimentLabels> {
  const images = MOCK_SESSION_IMAGES.filter((img) => img.experimentId === experimentId);
  const labels = images
    .filter((img) => img.label !== null)
    .map((img) => ({ imageKey: img.imageKey, label: img.label as string }));
  const sessions = labels.length
    ? (MOCK_SESSIONS[experimentId] ?? []).map((session) => ({ ...session, labels }))
    : [];
  return delay(200).then(() => ({
    experimentId,
    sessions,
    images: sessions.length ? images : [],
  }));
}

export async function labelizeListExperiments(
  _http?: HttpClient,
  _signal?: AbortSignal
): Promise<LabelingExperiment[]> {
  return [
    { experimentId: '10071', toolIds: ['1', '2', '3'] },
    { experimentId: '10082', toolIds: ['1'] },
    { experimentId: '20031', toolIds: ['1', '2'] },
  ];
}

export function labelizeSaveLabels(
  _http: HttpClient,
  sessionId: string,
  labels: Record<string, ImageLabel | null>,
  _signal?: AbortSignal
): Promise<{ sessionId: string; updatedCount: number }> {
  return delay(100).then(() => ({
    sessionId,
    updatedCount: Object.keys(labels).length,
  }));
}

export function labelizeDeleteLabel(
  _http: HttpClient,
  _sessionId: string,
  _imageKey: string,
  _signal?: AbortSignal
): Promise<{ success: boolean }> {
  return delay(100).then(() => ({ success: true }));
}

export function labelizeListPredictions(): Promise<never[]> {
  return Promise.resolve([]);
}
